'use client';

import { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { useRouter } from 'next/navigation';

interface SimpleUser {
  firstName?: string;
  lastName?: string;
  employeeCode?: string | number;
}

interface UserContextType {
  isAdmin: boolean;
  isAuthenticated: boolean;
  employeeCode: string | null;
  user: SimpleUser | null;
  loading: boolean;
  login: (role: string, employeeCode: string, user: SimpleUser) => void;
  logout: () => Promise<void>;
}


const UserContext = createContext<UserContextType | undefined>(undefined);

function safeGetLocalStorage(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSetLocalStorage(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // ignore
  }
}

function safeRemoveLocalStorage(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch {
    // ignore
  }
}

export const UserProvider = ({ children }: { children: React.ReactNode }) => {
  const [isAdmin, setIsAdmin] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [employeeCode, setEmployeeCode] = useState<string | null>(null);
  const [user, setUser] = useState<SimpleUser | null>(null);
  const [loading, setLoading] = useState(true); // تعریف state برای loading
  const router = useRouter();
  const idleTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const logout = useCallback(async () => {
    // 1) ابتدا از سرور بخواه sid و سشن DB را پاک کند
    try {
      await fetch("/api/logout", {
        method: "POST",
        credentials: "include",
      });
    } catch {
      // حتی اگر شبکه مشکل داشت، باز هم خروج UI انجام شود
    }

    // 2) پاکسازی وضعیت‌های کلاینتی
    safeRemoveLocalStorage("role");
    safeRemoveLocalStorage("employeeCode");
    safeRemoveLocalStorage("user");

    setIsAuthenticated(false);
    setIsAdmin(false);
    setEmployeeCode(null);
    setUser(null);

    if (idleTimeoutRef.current) {
      clearTimeout(idleTimeoutRef.current);
    }

    // 3) ریدایرکت قطعی (از router استفاده نکن تا state گیر نکند)
    window.location.href = "/login";
  }, []);


  const resetIdleTimer = useCallback(() => {
    if (idleTimeoutRef.current) {
      clearTimeout(idleTimeoutRef.current);
    }
    idleTimeoutRef.current = setTimeout(() => {
      void logout();
    }, 5 * 60 * 60 * 1000);
  }, [logout]);

  const login = useCallback((role: string, employeeCode: string, userObj: SimpleUser) => {
    safeSetLocalStorage("role", role);
    safeSetLocalStorage("employeeCode", employeeCode);
    safeSetLocalStorage("user", JSON.stringify(userObj));

    // document.cookie = `employeeCode=${employeeCode}; path=/; SameSite=Lax`;

    setIsAuthenticated(true);
    setIsAdmin(role === "admin" || role === "adminv2");
    setEmployeeCode(employeeCode);
    setUser(userObj);

    resetIdleTimer();
  }, [resetIdleTimer]);


  useEffect(() => {
    let cancelled = false;

    const bootstrap = async () => {
      const role = safeGetLocalStorage('role');
      const employeeCode = safeGetLocalStorage('employeeCode');
      const storedUserRaw = safeGetLocalStorage('user');

      if (role && employeeCode && storedUserRaw) {
        let parsedUser: SimpleUser | null = null;
        try {
          parsedUser = JSON.parse(storedUserRaw);
        } catch {
          parsedUser = null;
        }

        setIsAuthenticated(true);
        setIsAdmin(role === 'admin' || role === 'adminv2');
        setEmployeeCode(employeeCode);
        setUser(parsedUser);
        resetIdleTimer();
      } else {
        // localStorage خالی یا ناقص است (مثلاً یک تب قدیمی که این مقادیر را از دست داده).
        // اگر نشست سرور (کوکی sid) هنوز معتبر باشد، اطلاعات کاربر را از سرور می‌گیریم
        // تا کاربر مجبور به خروج و ورود دوباره نشود.
        try {
          const res = await fetch('/api/employees/settings', { credentials: 'include' });
          if (!cancelled && res.ok) {
            const data = await res.json();
            const resolvedCode = String(data?.employeeCode || '').trim();
            if (resolvedCode) {
              login(String(data?.role || ''), resolvedCode, {
                firstName: data?.firstName,
                lastName: data?.lastName,
                employeeCode: resolvedCode,
              });
            }
          }
        } catch {
          // نشست سرور هم معتبر نیست؛ کاربر باید مثل قبل وارد شود.
        }
      }

      if (!cancelled) setLoading(false);
    };

    bootstrap();

    window.addEventListener('mousemove', resetIdleTimer);
    window.addEventListener('keydown', resetIdleTimer);

    return () => {
      cancelled = true;
      window.removeEventListener('mousemove', resetIdleTimer);
      window.removeEventListener('keydown', resetIdleTimer);
      if (idleTimeoutRef.current) {
        clearTimeout(idleTimeoutRef.current);
      }
    };
  }, [router, resetIdleTimer, login]);

  return (
    <UserContext.Provider value={{ isAdmin, isAuthenticated, employeeCode, user, loading, login, logout }}>
      {children}
    </UserContext.Provider>
  );
};

export const useUserContext = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUserContext باید در داخل UserProvider استفاده شود');
  }
  return context;
};
